"use client";

import React, { useRef, useState } from "react";
import Image from "next/image";
import { Link } from "@/i18n/navigation";
import { useLocale } from "next-intl";

// Icons
import { MdArrowForward, MdOutlineChevronRight } from "react-icons/md";

import styles from "./explore-beyond-banner.module.css";

const DESTINATIONS_DATA = [
  {
    id: "es",
    name: "Spania",
    properties: "12,045 proprietăți",
    image: "/placeholder-images/coastal-living.png",
  },
  {
    id: "it",
    name: "Italia",
    properties: "5,876 proprietăți",
    image: "/placeholder-images/city-living.png",
  },
  {
    id: "it2",
    name: "Italia",
    properties: "5,876 proprietăți",
    image: "/placeholder-images/city-living.png",
  },
  {
    id: "ae",
    name: "Emiratele Arabe Unite",
    properties: "15,672 proprietăți",
    image: "/placeholder-images/city-living.png",
  },
  {
    id: "ae2",
    name: "Emiratele Arabe Unite",
    properties: "15,672 proprietăți",
    image: "/placeholder-images/city-living.png",
  },
  {
    id: "fr",
    name: "Franța",
    properties: "8,312 proprietăți",
    image: "/placeholder-images/countryside-living.png",
  },
  {
    id: "pt",
    name: "Portugalia",
    properties: "6,421 proprietăți",
    image: "/placeholder-images/coastal-living.png",
  },
  {
    id: "pt2",
    name: "Portugalia",
    properties: "6,421 proprietăți",
    image: "/placeholder-images/coastal-living.png",
  },
];

export default function ExploreBeyondBanner() {
  const locale = useLocale();
  const isRTL = locale === "ar" || locale === "he";

  const scrollRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [hasDragged, setHasDragged] = useState(false);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!scrollRef.current) return;
    setIsDragging(true);
    setHasDragged(false);
    setStartX(e.pageX - scrollRef.current.offsetLeft);
    setScrollLeft(scrollRef.current.scrollLeft);
  };

  const handleMouseLeave = () => setIsDragging(false);

  const handleMouseUp = () => {
    setIsDragging(false);
    setTimeout(() => setHasDragged(false), 50);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !scrollRef.current) return;
    e.preventDefault();

    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    scrollRef.current.scrollLeft = scrollLeft - walk;

    if (Math.abs(walk) > 5) {
      setHasDragged(true);
    }
  };

  return (
    <section className={styles.section}>
      <div className={styles.bannerWrapper}>
        {/* Left Side: Text & Button */}
        <div className={styles.textSide}>
          <h2 className={styles.title}>
            Vrei să explorezi dincolo
            <br />
            de România?
          </h2>
          <p className={styles.subtitle}>
            Descoperă proprietăți în alte țări și găsește oportunități
            internaționale.
          </p>
          <Link href="/internationale-immobilien" className={styles.exploreBtn}>
            Explorează la nivel global
            <MdArrowForward className={styles.btnIcon} />
          </Link>
        </div>

        {/* Right Side: Native Drag Slider */}
        <div className={styles.sliderSide}>
          <div
            className={`${styles.cardsTrack} ${isDragging ? styles.dragging : ""}`}
            ref={scrollRef}
            dir={isRTL ? "rtl" : "ltr"}
            onMouseDown={handleMouseDown}
            onMouseLeave={handleMouseLeave}
            onMouseUp={handleMouseUp}
            onMouseMove={handleMouseMove}
          >
            {DESTINATIONS_DATA.map((dest) => (
              <div
                key={dest.id}
                className={styles.cardWrapper}
                onClickCapture={(e) => {
                  if (hasDragged) {
                    e.stopPropagation();
                    e.preventDefault();
                  }
                }}
              >
                <Link
                  href={`/destinations/${dest.id}`}
                  className={styles.card}
                  draggable={false}
                >
                  <div className={styles.imageContainer}>
                    <Image
                      src={dest.image}
                      alt={dest.name}
                      fill
                      sizes="(max-width: 768px) 50vw, 25vw"
                      className={styles.cardImage}
                      draggable={false} /* Blocks native ghost image drag */
                    />
                  </div>
                  <div className={styles.cardOverlay}>
                    <div className={styles.cardContent}>
                      <div className={styles.textData}>
                        <h3 className={styles.cardTitle}>{dest.name}</h3>
                        <span className={styles.cardSubtitle}>
                          {dest.properties}
                        </span>
                      </div>
                      <div className={styles.iconButton}>
                        <MdOutlineChevronRight className={styles.chevronIcon} />
                      </div>
                    </div>
                  </div>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
